import { useCallback, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { selectDashboards } from "./dashboardSlice";
import { dashboardDataRequestThunk } from "./dashboardThunks";

export const useFetchFilteredPanelData = (dashboardId, panel) => {
    const dispatch = useDispatch();
    
    const panelKey = panel?.unique_id || panel?.id;
    const panelRef = useRef(panel);

    useEffect(() => {
        panelRef.current = panel;
    }, [panel]);

    const selectPanelAndFilters = useMemo(
        () => createSelector(
            [selectDashboards],
            (dashboards) => {
                const dashboard = dashboards[dashboardId];
                return {
                    panelObject: dashboard?.panels?.[panelKey] || null,
                    filterConfig: dashboard?.filters?.config || [],
                    filterValues: dashboard?.filters?.currentValues || {}
                };
            }
        ),
        [dashboardId, panelKey]
    );

    const { panelObject, filterConfig, filterValues } = useSelector(selectPanelAndFilters);

    const filterConfigRef = useRef(filterConfig);
    const filterValuesRef = useRef(filterValues);

    useEffect(() => {
        filterConfigRef.current = filterConfig;
        filterValuesRef.current = filterValues;
    }, [filterConfig, filterValues]);

    const fetchFilteredPanelData = useCallback(() => {
        dispatch(
            dashboardDataRequestThunk({ 
                dashboardId,
                panel: panelRef.current,
                filterConfig: filterConfigRef.current,
                filterValues: filterValuesRef.current
            })
        ).catch((err) => {
            console.error(`Failed to refresh panel ${panelKey}:`, err);
        });
    }, [dispatch, dashboardId, panelKey]);

    return { panelObject, fetchFilteredPanelData };
};
